import React, { ReactNode, useMemo } from 'react';

import { useSnackbar } from 'notistack';

import { ContentCopy } from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import FlightIcon from '@mui/icons-material/Flight';
import InfoIcon from '@mui/icons-material/Info';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Button, Chip, IconButton, Paper, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { getBundleStatusInfo, getSurchargeSplit } from '~/components/Accommodation/utils/rates';

import { MergedRate, Rate } from '~/services/AccommodationService';

import { RoomRatesWithRates } from './AccommodationPropertyRatesSection';
import CancellationBadge from './CancellationBadge';
import CancellationPolicyModal from './CancellationPolicyModal';
import GroupedRates from './DebugInformation/GroupedRates';
import MealPlanChip from './MealPlanChip';
import OriginalPrice from './OriginalPrice';
import RateDetailsModal from './RateDetailsModal';
import RateInclusionsModal from './RateInclusionsModal';
import RateTypeBadge from './RateTypeBadge';
import RoomFacilitiesModal from './RoomFacilitiesModal';
import { getMergedRateKeyForWinningRate } from './mergedRateKeys';
import { formatMargin, formatPrice } from './utils/formatPrice';
import {
  describeLuxPlusWithdrawal,
  describeMarginUplift,
  describePriceMatch,
  formatMarginChange,
  getPriceMatchDetails,
} from './utils/priceMatch';

const RATES_GRID_SX = {
  minWidth: 0,
  '& .MuiDataGrid-cell': {
    py: 1,
    alignItems: 'center',
  },
  '& .MuiDataGrid-cellContent': {
    overflowWrap: 'anywhere',
  },
};

const FULL_SCREEN_WIDTH_OVERRIDE_STYLES = {
  position: 'relative',
  left: '50% !important',
  right: '50% !important',
  marginLeft: '-50vw !important',
  marginRight: '-50vw !important',
  width: '100vw !important',
  paddingLeft: '24px !important',
  paddingRight: '24px !important',
  boxSizing: 'border-box !important',
};

type RatesLayoutVariant = 'classic' | 'sidebar';

export default function AccommodationPropertyRatesResultsTable({
  roomRates,
  openModal,
  groupedMergedRates,
  currency,
  timezone,
  variant = 'classic',
}: {
  roomRates: RoomRatesWithRates;
  openModal: (title: string, content: ReactNode) => void;
  groupedMergedRates: Record<string, Array<MergedRate>>;
  currency: string;
  timezone: string;
  variant?: RatesLayoutVariant;
}) {
  const theme = useTheme();
  const isSidebar = variant === 'sidebar';

  const { enqueueSnackbar } = useSnackbar();

  const sortedRoomsWithRates = useMemo(() => {
    return Object.entries(roomRates).sort((a, b) => a[1].sort - b[1].sort);
  }, [roomRates]);

  const handleCancellationPolicyClick = (rate: Rate) => {
    openModal(
      'Cancellation Policy Details',
      <CancellationPolicyModal rate={rate} currency={currency} timezone={timezone} />,
    );
  };

  const handleInclusionsClick = (
    inclusions: Rate['inclusions'],
    bonusInclusions: Rate['inclusions'],
    luxPlusInclusions?: Rate['inclusions'],
  ) => {
    openModal(
      'Rate Inclusions',
      <RateInclusionsModal
        inclusions={inclusions}
        bonusInclusions={bonusInclusions}
        luxPlusInclusions={luxPlusInclusions}
      />,
    );
  };

  const handleFacilitiesClick = (rate: Rate, roomName: string) => {
    openModal('Room Facilities', <RoomFacilitiesModal rate={rate} roomName={roomName} />);
  };

  const handleRateDetailsClick = (rate: Rate) => {
    openModal(
      'Rate Details',
      <RateDetailsModal
        rate={rate}
        currency={currency}
        mergedRates={groupedMergedRates[getMergedRateKeyForWinningRate(rate)] ?? []}
      />,
    );
  };

  const calculatePropertyFeesTotal = (propertyFees: Array<Rate['propertyFees'][number]> | undefined) => {
    if (!propertyFees || propertyFees.length === 0) {
      return 0;
    }
    return propertyFees.reduce((total, fee) => total + (fee.amount || 0), 0);
  };

  const renderInclusionsBadge = (
    inclusions: Rate['inclusions'],
    bonusInclusions: Rate['inclusions'],
    luxPlusInclusions?: Rate['inclusions'],
  ) => {
    if (
      (!inclusions || inclusions.length === 0) &&
      (!bonusInclusions || bonusInclusions.length === 0) &&
      (!luxPlusInclusions || luxPlusInclusions.length === 0)
    ) {
      return (
        <Button size="small" variant="text" color="primary" disabled>
          None
        </Button>
      );
    }

    return (
      <Button
        size="small"
        variant="text"
        color="primary"
        onClick={() => handleInclusionsClick(inclusions, bonusInclusions, luxPlusInclusions)}
        startIcon={<InfoIcon />}
      >
        View
      </Button>
    );
  };

  const handleCopyClick = (id: string) => {
    navigator.clipboard.writeText(id);
    enqueueSnackbar(`Copied to clipboard`, { variant: 'success' });
  };

  // Define columns for DataGrid
  const columns: Array<GridColDef> = [
    {
      field: 'rateType',
      headerName: 'Rate Type',
      ...(isSidebar ? { minWidth: 90, flex: 0.7 } : { flex: 1 }),
      renderCell: (params) => (
        <Box>
          <RateTypeBadge rate={params.row} />
        </Box>
      ),
    },
    {
      field: 'name',
      headerName: 'Name',
      ...(isSidebar ? { minWidth: 140 } : {}),
      flex: 1.5,
    },
    {
      field: 'flights',
      headerName: 'Flights',
      ...(isSidebar ? { width: 72 } : { flex: 0.75 }),
      renderCell: (params) => {
        const bundleStatusInfo = getBundleStatusInfo(params.row.meta);
        return (
          <Box>
            {bundleStatusInfo && (
              <Tooltip title={bundleStatusInfo.description}>
                <Chip icon={<FlightIcon />} size="small" color={bundleStatusInfo.color} sx={{ mt: 0.5 }} />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'mealPlan',
      headerName: 'Meal Plan',
      ...(isSidebar ? { minWidth: 130, flex: 0.9 } : { flex: 1.4 }),
      renderCell: (params) => <MealPlanChip boardCode={params.row.boardCode} openModal={openModal} />,
    },
    {
      field: 'cancellation',
      headerName: 'Cancellation',
      ...(isSidebar ? { minWidth: 120, flex: 0.9 } : { flex: 1 }),
      renderCell: (params) => <CancellationBadge rate={params.row} onClick={handleCancellationPolicyClick} />,
    },
    {
      field: 'inclusions',
      headerName: 'Inclusions',
      ...(isSidebar ? { width: 100 } : { flex: 1 }),
      renderCell: (params) =>
        renderInclusionsBadge(params.row.inclusions, params.row.bonusInclusions, params.row.luxPlusInclusions),
    },
    {
      field: 'bonusInclusions',
      headerName: 'Bonus Inc',
      ...(isSidebar ? { width: 96 } : { flex: 1 }),
      renderCell: (params) =>
        params.row.bonusInclusions.length > 0 ? (
          <Chip icon={<CheckCircleIcon />} size="small" color="success" />
        ) : (
          <Chip icon={<CloseIcon />} size="small" />
        ),
    },
    {
      field: 'basePrice',
      headerName: 'Base Price',
      ...(isSidebar ? { minWidth: 105, flex: 0.8 } : { flex: 1 }),
      renderCell: (params) => formatPrice(params.row.total?.beforeTaxesFeesPrice as number, currency),
    },
    {
      field: 'taxesAndFees',
      headerName: 'Taxes & Fees',
      ...(isSidebar ? { minWidth: 110, flex: 0.8 } : { flex: 1 }),
      renderCell: (params) => formatPrice(params.row.total?.taxesAndFees as number, currency),
    },
    {
      field: 'propertyFees',
      headerName: 'Payable at Property',
      flex: isSidebar ? 0.9 : 1.2,
      minWidth: isSidebar ? 125 : 150,
      renderCell: (params) => {
        // Property fees alone understate this: a supplier surcharge flagged `payableAtProperty`
        // is collected by the property too, and is on `displayPrice` but not `sellingPrice`.
        const propertyFees = (params.row.total?.propertyFees as number) ?? 0;
        const { payableAtProperty } = getSurchargeSplit(params.row);
        const total = propertyFees + payableAtProperty;
        if (total <= 0) {
          return 'None';
        }
        // One figure can be two different charges, so the icon breaks it down on hover.
        const needsBreakdown = propertyFees > 0 && payableAtProperty > 0;
        return (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <span>{formatPrice(total, currency)}</span>
            <Tooltip
              title={
                <Box>
                  <Typography variant="caption" component="div" fontWeight="bold">
                    Payable directly to the property
                  </Typography>
                  {propertyFees > 0 && (
                    <Typography variant="caption" component="div">
                      Property fees: {formatPrice(propertyFees, currency)}
                    </Typography>
                  )}
                  {payableAtProperty > 0 && (
                    <Typography variant="caption" component="div">
                      Extra guest surcharge: {formatPrice(payableAtProperty, currency)}
                    </Typography>
                  )}
                  {needsBreakdown && (
                    <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                      Total {formatPrice(total, currency)}
                    </Typography>
                  )}
                </Box>
              }
            >
              {/* `display: block` drops the inline baseline gap under the SVG, which otherwise
                  sits the icon a couple of pixels below the price text. */}
              <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help', display: 'block' }} />
            </Tooltip>
          </Stack>
        );
      },
    },
    {
      field: 'totalPrice',
      headerName: 'Total Price',
      ...(isSidebar ? { minWidth: 130, flex: 0.8 } : { flex: 1.2 }),
      renderCell: (params) => {
        const displayPrice = params.row.total?.displayPrice || 0;
        const price = formatPrice(displayPrice, currency);
        // HP-2812: a dynamically matched rate is selling below what it was priced at, funded out
        // of its own margin. Striking through the old price is what makes that legible at a
        // glance — the figure on its own looks like any other rate's.
        const priceMatch = getPriceMatchDetails(params.row, {
          displayPrice,
          mergedRates: groupedMergedRates[getMergedRateKeyForWinningRate(params.row)] ?? [],
        });
        if (!priceMatch) {
          return price;
        }
        const marginChange = priceMatch.marginChange && formatMarginChange(priceMatch.marginChange);
        const uplift = describeMarginUplift(priceMatch);
        return (
          <Tooltip
            title={
              <>
                <Box>{describePriceMatch(priceMatch, currency, { includeLabel: true })}</Box>
                {marginChange && (
                  <Box sx={{ mt: 0.5 }}>
                    {`Margin: ${marginChange.dollars}`}
                    {marginChange.percent != null && ` (${marginChange.percent})`}
                  </Box>
                )}
                {uplift != null && <Box sx={{ mt: 0.5 }}>{uplift}</Box>}
              </>
            }
          >
            <Box component="span">
              {price}
              {priceMatch.originalDisplayPrice != null && (
                <OriginalPrice price={priceMatch.originalDisplayPrice} currency={currency} />
              )}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'luxplusPrice',
      headerName: 'Luxplus Price',
      ...(isSidebar ? { minWidth: 110, flex: 0.8 } : { flex: 1 }),
      renderCell: (params) => {
        // A withdrawal deletes `luxPlus`, which the `|| 0` below would render as "A$0" — a price,
        // and indistinguishable from a rate that never had a member price.
        const priceMatch = getPriceMatchDetails(params.row);
        if (priceMatch?.luxPlus.withdrawn) {
          return (
            <Tooltip title={describeLuxPlusWithdrawal(priceMatch) ?? ''}>
              <Box component="span" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                Withdrawn
                {priceMatch.luxPlus.originalDisplayPrice != null && (
                  <OriginalPrice price={priceMatch.luxPlus.originalDisplayPrice} currency={currency} />
                )}
              </Box>
            </Tooltip>
          );
        }
        return formatPrice(params.row.luxPlus?.displayPrice || 0, currency);
      },
    },
    {
      field: 'valuedUpTo',
      headerName: 'Valued Up To',
      ...(isSidebar ? { minWidth: 110, flex: 0.8 } : { flex: 1 }),
      renderCell: (params) => {
        return formatPrice(params.row.total.valuedUpToPrice, currency);
      },
    },
    {
      field: 'savings',
      headerName: 'Savings',
      ...(isSidebar ? { minWidth: 100, flex: 0.7 } : { flex: 1 }),
      renderCell: (params) => {
        return params.row.total?.discount > 0 ? (
          <Chip label={`${(params.row.total.discount * 100).toFixed(0)}% off`} color="secondary" size="small" />
        ) : (
          'No discount'
        );
      },
    },
    {
      field: 'margin',
      headerName: 'Margin',
      ...(isSidebar ? { width: 92 } : { flex: 1 }),
      renderCell: (params) => {
        return formatMargin(params.row.total.margin);
      },
    },
    {
      field: 'details',
      headerName: 'Details',
      width: 80,
      sortable: false,
      renderCell: (params) => (
        <IconButton size="small" color="primary" onClick={() => handleRateDetailsClick(params.row)}>
          <InfoIcon />
        </IconButton>
      ),
    },
    {
      field: 'supplier',
      headerName: 'Supplier',
      flex: isSidebar ? 0.8 : 1,
      minWidth: isSidebar ? 110 : 120,
      // Bedbank rates carry the supplier on meta; reservation rates are direct LPC and have none.
      valueGetter: (_value, row) => (row.meta && 'supplier' in row.meta ? row.meta.supplier : 'LPC'),
      renderCell: (params) => {
        const supplier = params.row.meta && 'supplier' in params.row.meta ? params.row.meta.supplier : 'LPC';
        return <Chip label={supplier} size="small" variant="outlined" sx={{ maxWidth: '100%' }} />;
      },
    },
    {
      field: 'mergedRates',
      headerName: 'Merged',
      width: 110,
      sortable: false,
      renderCell: (params) => {
        const mergedRates = groupedMergedRates[getMergedRateKeyForWinningRate(params.row)] ?? [];
        return <GroupedRates winningRate={params.row} mergedRates={mergedRates} currency={currency} />;
      },
    },
  ];

  return (
    <>
      {sortedRoomsWithRates.map(([roomId, room]) => (
        <Paper
          key={roomId}
          sx={{
            mb: 3,
            overflow: 'hidden',
            ...(isSidebar ? { width: '100%', maxWidth: '100%' } : FULL_SCREEN_WIDTH_OVERRIDE_STYLES),
          }}
        >
          <Box sx={{ p: 2, bgcolor: theme.palette.grey[100] }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="column" alignItems="flex-start">
                <Typography variant="h6">{room.name}</Typography>
                <Typography variant="subtitle2">
                  ID: {roomId}
                  <IconButton title="Copy Room ID" onClick={() => handleCopyClick(roomId)}>
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Typography>
              </Stack>
              <Button size="small" variant="outlined" onClick={() => handleFacilitiesClick(room.rates?.[0], room.name)}>
                View Facilities
              </Button>
            </Stack>
          </Box>

          {room.rates?.length > 0 && (
            <DataGrid
              rows={room.rates || []}
              columns={columns}
              getRowId={(row) => row.id}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 5 },
                },
              }}
              pageSizeOptions={[5, 10, 20]}
              disableColumnFilter
              disableColumnMenu
              disableDensitySelector
              disableColumnSelector
              hideFooterSelectedRowCount
              sx={isSidebar ? RATES_GRID_SX : undefined}
            />
          )}
          {room.rates?.length === 0 && (
            <Box sx={{ p: 2, bgcolor: theme.palette.grey[100], textAlign: 'center' }}>
              <Typography variant="subtitle1">No rates found for this room</Typography>
            </Box>
          )}
        </Paper>
      ))}
    </>
  );
}
